import { Injectable } from '@angular/core';
import {
  BlockVariant,
  ButtonBlockProps,
  CommentBlockProps,
  DynamicBlock,
  DynamicBlocks,
  InputTextareaBlockProps,
  TextBlockProps,
} from '@/components/block-builder/block-builder.types';
import { AgpmDetails } from '@/types';
import { BehaviorSubject } from 'rxjs';
import _ from 'lodash';

interface getPreDraftAgpmBlocksProps {
  save?: () => void;
  reset?: () => void;
}

interface getInspectAgpmBlocksProps {
  refreshCount: number;
  status?: string;
  refresh?: () => void;
  accessHistory?: () => void;
  downloadSigned?: () => void;
}

interface getDraftAgpmBlocksProps {
  refreshCount: number;
  startWorkflow?: () => void;
  save?: () => void;
  reset?: () => void;
  accessHistory?: () => void;
  loadingUpdate?: BehaviorSubject<boolean>;
  loadingValidation?: BehaviorSubject<boolean>;
}

interface getFlowableAgpmBlocksProps {
  refreshCount: number;
  task?: Pick<AgpmDetails, 'taskName' | 'nextSteps' | 'taskId' | 'traceability'>;
  validate?: (value: string) => void;
  publish?: () => void;
  //ac validation **********************
  openDownloadDialog?: () => void;
  openUploadDialog?: () => void;
  // **********************************
  accessHistory?: () => void;
  inspectMode?: boolean;
  comment?: BehaviorSubject<string>;
  onCommentChange?: (value: string) => void;
  error?: BehaviorSubject<string | null>;
}

enum TaskVariant {
  VALIDATION = 'Validation (AC)',
  PUBLICATION = '(AC) Publication',
  RECTIFICATION = 'Rectification (AC)',
}

@Injectable({
  providedIn: 'root',
})
export class AgpmWorkflowBlocksService {
  accessHistoryButton = (accessHistory?: () => void): DynamicBlock => {
    return {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.actions.access_history',
        onClick: accessHistory,
        severity: 'secondary',
      },
    };
  };

  refreshedLabel = (refreshCount: number): DynamicBlock<TextBlockProps> => {
    if (refreshCount === 0)
      return {
        variant: BlockVariant.CUSTOM,
      };
    else
      return {
        variant: BlockVariant.TEXT,
        props: {
          blocks: [
            {
              content: refreshCount === 1 ? 'Ce document a été actualisé une seule fois' : `Ce document a été actualisé ${refreshCount} fois`,
              class: 'text-[1rem] font-thin opacity-70 no-select italic',
            },
          ],
          orientation: 'horizontal',
          gap: 1,
        },
        class: 'w-fit mx-auto',
      };
  };

  getInspectAgpmBlocks = ({ status, refreshCount, accessHistory, downloadSigned, refresh }: getInspectAgpmBlocksProps): DynamicBlocks[] => {
    const finalBlocks: DynamicBlocks[] = [];

    const refreshButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.sideActions.refresh',
        onClick: () => refresh?.(),
      },
    };

    const downloadSignedButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.sideActions.download_model',
        onClick: () => downloadSigned?.(),
        severity: 'info',
      },
    };

    finalBlocks.push({});
    if (status === 'PUBLIE') {
      finalBlocks[0]['0'] = [refreshButton];
    }
    if (downloadSigned) finalBlocks[0]['1'] = [downloadSignedButton];
    if (_.isEqual(finalBlocks[0], {})) finalBlocks.splice(0, 1);

    finalBlocks.push({
      '0': [this.accessHistoryButton(accessHistory)],
      '1': [this.refreshedLabel(refreshCount)],
    });

    return finalBlocks;
  };

  getPreDraftAgpmBlocks = ({ save, reset }: getPreDraftAgpmBlocksProps): DynamicBlocks => {
    const saveButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.sideActions.save',
        onClick: save,
      },
    };
    const resetButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.sideActions.reset',
        onClick: reset,
        severity: 'secondary',
      },
    };
    return {
      '0': [saveButton],
      '1': [resetButton],
    };
  };

  getDraftAgpmBlocks = ({ startWorkflow, save, loadingValidation, loadingUpdate, accessHistory }: getDraftAgpmBlocksProps): DynamicBlocks[] => {
    const updateButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.sideActions.update',
        onClick: () => save?.(),
        disabled: loadingUpdate,
      },
    };
    const startWorkflowButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'agpm.sideActions.passForValidation',
        onClick: () => startWorkflow?.(),
        loading: loadingValidation,
      },
    };

    return [
      {
        '0': [updateButton],
        '1': [startWorkflowButton],
      },
      {
        '0': [this.accessHistoryButton(accessHistory)],
      },
    ];
  };

  getFlowableAgpmBlocks = ({
    refreshCount,
    task,
    validate,
    openDownloadDialog,
    openUploadDialog,
    comment,
    onCommentChange,
    publish,
    inspectMode,
    accessHistory,
  }: getFlowableAgpmBlocksProps): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [];
    let renderTextArea = false;
    let renderComment = false;

    if (task?.taskName == TaskVariant.VALIDATION) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          if (`${step.validationValue}` == 'true') {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Télécharger`,
                severity: 'success',
                onClick: openDownloadDialog,
              },
            });
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Déposer`,
                severity: 'secondary',
                onClick: openUploadDialog,
              },
            });
          } else {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `agpm.sideActions.${step.actionLabel}`,
                onClick: () => validate?.(step.validationValue),
              },
            });
          }
        });
      renderTextArea = true;
    } else if (task?.taskName == TaskVariant.PUBLICATION) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: 'agpm.sideActions.publish',
          onClick: () => publish?.(),
        },
      });
      renderComment = true;
      renderTextArea = true;
    } else if (task?.taskName == TaskVariant.RECTIFICATION) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: step.actionLabel,
              onClick: () => validate?.(step.validationValue),
            },
          });
        });
      renderComment = true;
      renderTextArea = true;
    }

    const textareablock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'agpm.sideActions.comment.title',
        rows: 5,
        value: comment?.getValue(),
        placeholder: 'agpm.sideActions.comment.placeholder',
        onChange: (value: string) => onCommentChange?.(value),
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: task?.traceability?.comment || '',
        date: task?.traceability?.dateComment || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const finalBlock: DynamicBlocks = {};
    if (renderComment) finalBlock['0'] = [commentBlock];
    if (blocks.length > 0 && !inspectMode) finalBlock['1'] = blocks;
    if (renderTextArea && !inspectMode) finalBlock['2'] = [textareablock];
    return [
      finalBlock,
      {
        '0': [this.accessHistoryButton(accessHistory)],
        '1': [this.refreshedLabel(refreshCount)],
      },
    ];
  };
}
